import { useMemo } from 'react'

import {
  Box,
  Text,
  Stack,
  Textarea,
  Select,
  Icon,
  HStack,
  Badge,
} from '@chakra-ui/react'
import { useField, useFormikContext } from 'formik'
import { InfoIcon } from 'lucide-react'
import { useSelector } from 'react-redux'

import {
  AddressField,
  ContactInfoField,
  DistrictLockField,
  GroupsField,
  LanguagesField,
  NewsletterSubscribedField,
  TagsField,
} from './fields'
import BusinessAddressField from './fields/BusinessAddressField'
import { districtOptions } from '../../../../constants'
import { hasPermission } from '../../../../utils'
import { FormField, ShowHideFormField } from '../../../ui'

const Section = ({ heading, isRequired, children }) => {
  return (
    <Box>
      {typeof heading === 'string' ? (
        <Text variant='subheading-blue' mb={1}>
          {heading} {isRequired && <span color='red'>*</span>}
        </Text>
      ) : (
        heading
      )}
      <Box>{children}</Box>
    </Box>
  )
}

const ConstituentFormInputs = ({ field = '' }) => {
  const { values: formikValues } = useFormikContext()

  const user = useSelector(state => state.auth.user)
  const canEditDistrict = hasPermission(
    user,
    'directory.constituent.edit_district'
  )

  // formikField is not used if field is empty, so name is just a placeholder
  const [formikField] = useField(field || 'name')
  const values = useMemo(
    () => (field ? formikField.value : formikValues) || {},
    [field, formikValues, formikField]
  )

  field = field ? field + '.' : field

  return (
    <Box p={1} overflowY='auto'>
      <Stack gap={5}>
        {canEditDistrict && (
          <Section heading='Constituent District'>
            {formikValues.district && field.includes('newConstituent') ? (
              <Text>
                Defaulted to casework district:{' '}
                <Badge>District {formikValues.district}</Badge>
              </Text>
            ) : (
              <FormField
                field={field + 'district'}
                placeholder='Select district...'
                inputType={Select}
                options={districtOptions}
              />
            )}
          </Section>
        )}
        <Section heading='Category'>
          <FormField
            field={field + 'category'}
            placeholder={null}
            inputType={Select}
            options={['Individual', 'Business']}
          />
        </Section>
        <Section heading='Privacy'>
          <DistrictLockField field={field + 'visible_to_district_only'} />
        </Section>

        {/* Constituent Name */}
        {values.category === 'Individual' ? (
          <Section heading='Personal Information'>
            <Stack>
              <Box>
                <FormField field={field + 'name'} placeholder='Full Name' />

                <HStack
                  color='blackAlpha.700'
                  _dark={{ color: 'whiteAlpha.700' }}
                  pl={1}
                >
                  <Icon as={InfoIcon} />
                  <Text>Leave name blank if anonymous</Text>
                </HStack>
              </Box>

              <HStack direction={{ base: 'column', sm: 'row' }} align='start'>
                <FormField
                  field={field + 'title'}
                  placeholder='Title'
                  shouldOptimize
                />
                <FormField
                  field={field + 'suffix'}
                  placeholder='Suffix'
                  shouldOptimize
                />
              </HStack>
              <HStack align='start'>
                <FormField
                  field={field + 'preferred_name'}
                  placeholder='Preferred Name'
                  shouldOptimize
                />
                <FormField
                  field={field + 'pronouns'}
                  placeholder='Pronouns'
                  shouldOptimize
                />
              </HStack>
            </Stack>
          </Section>
        ) : (
          <Section heading='Business Information'>
            <FormField field={field + 'name'} placeholder='Business Name' />
          </Section>
        )}

        {/* Constituent Contact Information */}
        <Section heading='Contact Information'>
          <NewsletterSubscribedField field={field + 'newsletter_subscribed'} />
          <ContactInfoField field={field + 'contact_info'} />
        </Section>

        {/* Constituent Address Information */}
        {/* HOME ADDRESS */}
        {values.category === 'Individual' && (
          <Section heading='Home Address Information'>
            <AddressField field={field} type='home' />
          </Section>
        )}
        {/* BUSINESS ADDRESS */}
        {values.category === 'Individual' ? (
          <BusinessAddressField field={field} />
        ) : (
          <Section heading='Business Address Information'>
            <AddressField field={field} type='business' />
          </Section>
        )}

        {/* LANGUAGES */}
        <LanguagesField field={field + 'languages'} />

        {/* Tags */}
        <TagsField field={field + 'tags'} district={formikValues.district} />

        {/* GROUPS */}
        <Box>
          <GroupsField
            field={field + 'groups'}
            district={formikValues.district}
          />
        </Box>

        {/* NOTES */}
        <ShowHideFormField heading='Notes' defaultShow={!!values.notes}>
          <FormField
            field={field + 'notes'}
            placeholder='Record any additional notes about constituent, if needed'
            inputType={Textarea}
            shouldOptimize
          />
        </ShowHideFormField>
      </Stack>
    </Box>
  )
}

export default ConstituentFormInputs
